import { useEffect, useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { createRoot } from "react-dom/client";
import vehicleService from "../../services/vehicles/vehicleservice";
import { DriverInfoDto } from "../../models/Delivery-Executive/DeliveryExecutive";
import type { DriverAssignment } from "./route-preview";

type Props = {
  drivers: DriverInfoDto[];
  onClose: (result: { driverId: number; vehicleNo: string }[] | null) => void;
};

export default function VehiclePicker({ drivers, onClose }: Props) {
  const [vehicles, setVehicles] = useState<{ vehicleNumber: string }[]>([]);
  const [assignments, setAssignments] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const finished = useRef(false);
  const close = (result: { driverId: number; vehicleNo: string }[] | null) => {
    if (!finished.current) { finished.current = true; onClose(result); }
  };

  useEffect(() => {
    let cancelled = false;
    async function loadVehicles() {
      try {
        const resp = await vehicleService.getVehicleDropdown({});
        if (cancelled) return;
        const list = resp?.data ?? [];
        setVehicles(list);
        if (!list.length) setError("No vehicles available. Check vehicle access and try again.");
      } catch {
        if (!cancelled) setError("Unable to load vehicles. Close and try again.");
      } finally { if (!cancelled) setLoading(false); }
    }
    loadVehicles();
    return () => { cancelled = true; };
  }, []);

  const allSelected = !loading && drivers.length > 0 && drivers.every((d) =>
    vehicles.some(v => v.vehicleNumber === assignments[d.driverId]));

  return (

    <Dialog
      header="Assign Vehicle(s)"
      visible
      modal
      closable={false}
      style={{ width: "35rem", maxWidth: "95vw" }}
      onHide={() => close(null)}
    >
      {loading && <p role="status">Loading vehicles…</p>}
      {error && <p role="alert" className="text-red-600">{error}</p>}
      <div className="p-fluid" style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {drivers.map((d) => (
          <div
            key={d.driverId}
            className="mb-3 p-2 border-round"
            style={{ border: "1px solid #ddd", background: "#fafafa" }}
          >
            <label htmlFor={`vehicle-${d.driverId}`} className="font-semibold mb-2 block">
              {d.name} <span className="text-500">({d.phonenumber})</span>
            </label>

            <Dropdown
              inputId={`vehicle-${d.driverId}`}
              aria-label={`Vehicle for ${d.name}`}
              value={assignments[d.driverId] ?? ""}
              onChange={(e) => {
                const selectedVehicle = e.value;

                // Check if selected vehicle is already assigned
                const alreadyAssignedDriver = Object.entries(assignments).find(
                  ([driverId, vehicle]) => vehicle === selectedVehicle && Number(driverId) !== d.driverId
                );

                if (alreadyAssignedDriver) {
                  setError("Choose a different vehicle for each executive.");
                  return; // prevent assigning this vehicle
                }

                // Otherwise assign normally
                setError("");
                setAssignments((prev) => ({
                  ...prev,
                  [d.driverId]: selectedVehicle,
                }));
              }}
              options={vehicles}
              optionLabel="vehicleNumber"
              optionValue="vehicleNumber"
              placeholder="Choose Vehicle"
              className="w-full"
              filter

            />
          </div>
        ))}
      </div>

      <div className="flex justify-content-end gap-2 mt-3">
        <Button label="Cancel" onClick={() => close(null)} />
        <Button
          label="Review routes"
          disabled={!allSelected}
          onClick={() =>
            close(
              drivers.map((d) => ({
                driverId: d.driverId,
                vehicleNo: assignments[d.driverId],
              }))
            )
          }
        />
      </div>
    </Dialog>
  );
}

export function pickVehicles(drivers: DriverInfoDto[]): Promise<DriverAssignment[] | null> {
  const host = document.createElement("div"); document.body.appendChild(host);
  const root = createRoot(host);
  return new Promise(resolve => {
    root.render(<VehiclePicker drivers={drivers} onClose={result => {
      resolve(result ? drivers.map(driver => ({ driver, vehicleNo: result.find(r => r.driverId === driver.driverId)!.vehicleNo })) : null);
      window.setTimeout(() => { root.unmount(); host.remove(); }, 0);
    }} />);
  });
}
